<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n, LOCALES, type Locale } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const { t, locale, setLocale, locales } = useI18n()
const authStore = useAuthStore()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)
const credits = computed(() => authStore.credits)

// Theme state - use ref for reactivity
const currentTheme = ref('dark')

onMounted(() => {
  currentTheme.value = document.documentElement.getAttribute('data-theme') || 'dark'
})

function toggleTheme() {
  const newTheme = currentTheme.value === 'dark' ? 'light' : 'dark'
  currentTheme.value = newTheme
  document.documentElement.setAttribute('data-theme', newTheme)
  localStorage.setItem('m2v_theme', newTheme)
  if (isAuthenticated.value) {
    authStore.updateProfile({ theme_pref: newTheme })
  }
}

function handleLogout() {
  authStore.logout()
  router.push('/')
}

function changeLocale(newLocale: Locale) {
  setLocale(newLocale)
  if (isAuthenticated.value) {
    authStore.updateProfile({ locale: newLocale })
  }
}
</script>

<template>
  <header class="app-header">
    <div class="header-content">
      <!-- Logo -->
      <router-link to="/" class="logo">
        <img src="/official_logo_128.png" alt="M2V.io" class="logo-img" />
        <span class="logo-text">M2V.io</span>
      </router-link>

      <!-- Navigation -->
      <nav class="main-nav">
        <router-link to="/" class="nav-link">{{ t('nav.home') }}</router-link>
        <router-link to="/gallery" class="nav-link">{{ t('nav.gallery') }}</router-link>
        <router-link to="/pricing" class="nav-link">{{ t('nav.pricing') }}</router-link>
        <router-link v-if="isAuthenticated" to="/dashboard" class="nav-link">
          {{ t('nav.dashboard') }}
        </router-link>
      </nav>

      <!-- Right section -->
      <div class="header-actions">
        <!-- Theme toggle -->
        <button class="icon-btn" @click="toggleTheme" :title="t('profile.theme')">
          <span v-if="currentTheme === 'dark'">🌙</span>
          <span v-else>☀️</span>
        </button>

        <!-- Language selector -->
        <el-dropdown trigger="click" @command="changeLocale">
          <button class="icon-btn">
            {{ locales[locale].flag }}
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item 
                v-for="(info, code) in locales" 
                :key="code"
                :command="code"
                :class="{ active: locale === code }"
              >
                {{ info.flag }} {{ info.name }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <!-- Auth section -->
        <template v-if="isAuthenticated">
          <!-- Credits badge -->
          <router-link to="/pricing" class="credits-badge">
            <span class="credits-icon">💎</span>
            <span class="credits-count">{{ credits }}</span>
          </router-link>

          <!-- User menu -->
          <el-dropdown trigger="click">
            <button class="user-btn">
              <span class="user-avatar">{{ user?.display_name?.[0] || user?.email?.[0] || '?' }}</span>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="router.push('/profile')">
                  {{ t('nav.profile') }}
                </el-dropdown-item>
                <el-dropdown-item @click="router.push('/dashboard')">
                  {{ t('nav.dashboard') }}
                </el-dropdown-item>
                <el-dropdown-item divided @click="handleLogout">
                  {{ t('nav.logout') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>

        <template v-else>
          <router-link to="/login" class="login-btn">
            {{ t('nav.login') }}
          </router-link>
        </template>
      </div>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--m2v-bg-surface);
  border-bottom: 1px solid var(--m2v-border-subtle);
  backdrop-filter: blur(12px);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--m2v-space-6);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--m2v-space-6);
}

.logo {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-2);
  font-family: var(--m2v-font-display);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--m2v-text-primary);
  text-decoration: none;
}

.logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.logo-text {
  background: var(--m2v-gradient-hero);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.main-nav {
  display: flex;
  gap: var(--m2v-space-6);
}

.nav-link {
  color: var(--m2v-text-secondary);
  font-weight: 500;
  padding: var(--m2v-space-2) var(--m2v-space-3);
  border-radius: var(--m2v-radius-md);
  transition: all var(--m2v-transition-fast);
}

.nav-link:hover {
  color: var(--m2v-text-primary);
  background: var(--m2v-border-subtle);
}

.nav-link.router-link-active {
  color: var(--m2v-primary-light);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
}

.icon-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-md);
  cursor: pointer;
  font-size: 1.25rem;
  transition: all var(--m2v-transition-fast);
}

.icon-btn:hover {
  background: var(--m2v-border-subtle);
  border-color: var(--m2v-border-default);
}

.credits-badge {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-2);
  padding: var(--m2v-space-2) var(--m2v-space-3);
  background: var(--m2v-gradient-card);
  border: 1px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-full);
  color: var(--m2v-text-primary);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--m2v-transition-fast);
}

.credits-badge:hover {
  border-color: var(--m2v-primary);
}

.user-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-primary);
  border: none;
  border-radius: var(--m2v-radius-full);
  cursor: pointer;
  transition: all var(--m2v-transition-fast);
}

.user-btn:hover {
  transform: scale(1.05);
}

.user-avatar {
  color: white;
  font-weight: 600;
  text-transform: uppercase;
}

.login-btn {
  padding: var(--m2v-space-2) var(--m2v-space-5);
  background: var(--m2v-primary);
  color: white;
  font-weight: 600;
  border-radius: var(--m2v-radius-md);
  transition: all var(--m2v-transition-fast);
}

.login-btn:hover {
  background: var(--m2v-primary-dark);
  color: white;
}

@media (max-width: 768px) {
  .main-nav {
    display: none;
  }
  
  .header-content {
    padding: 0 var(--m2v-space-4);
  }
}
</style>
